import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import QuestionandanswerIcon from "../../../../../assets/svg/questionandanswer-icon";

import Button from "../../../../../components/Button/Button";
import Input from "../../../../../components/Input/Input";
import TextArea from "../../../../../components/TextArea/TextArea";
import AddIcon from "../../../../../assets/svg/add-icon";
import * as Yup from "yup";
import { getIn, useFormik } from "formik";
import TrashFilledIcon from "../../../../../assets/svg/trash-filled-icon";
import ThreeDotsVerticalIcon from "../../../../../assets/svg/three-dots-vertical-icon";
import { getFileTypeIcon } from "../../../../../utils/functions/file-icon-extractor";
// import SearchableDropdown from "../../../../../components/SearchableDropdown/SearchableDropdown";
import { getTransformPropertiesOptions } from "../../../utils/constants";
import { useCreateCustomQaMutation } from "../../../../../redux/services/playgroundApi";
import { customToast } from "../../../../../utils/custom-toast/custom-toast";
import { trackEvents } from "../../../../../utils/amplitude/amplitude";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store";
import SearchableDropdown from "../../../../../components/SearchableDropdown/SearchableDropdown";
import { SearchableDropdownOption } from "../../../../../components/SearchableDropdown/types";

interface AddCustomQAContentProps {
  onClose: () => void;
  setScrollToSection?: (scrollToSection: string | null) => void;
  properties: { res: { name: string; id: string }[] };
}

interface QAFormValues {
  qaItems: Array<{
    question: string;
    answer: string;
  }>;
}

const AddCustomQAContent = ({
  onClose,
  properties,
  setScrollToSection,
}: AddCustomQAContentProps) => {
  const orgId =
    useSelector((state: RootState) => state?.auth?.client?.client_code) ||
    "unknown";
  const userId =
    useSelector((state: RootState) => state?.auth?.user?._id) || "unknown";
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const kibanaMenuRef = useRef<HTMLDivElement>(null);
  const kibanaButtonRef = useRef<HTMLButtonElement>(null);
  const qaItemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [selectedProperty, setSelectedProperty] = useState<{
    id: string;
    name: string;
  } | null>({ id: "-1", name: "Generic" });

  const transformPropertiesOptions = getTransformPropertiesOptions(properties);
  const [createCustomQa, { isLoading: isCreatingCustomQa }] =
    useCreateCustomQaMutation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        kibanaMenuRef.current &&
        !kibanaMenuRef.current.contains(event.target as Node) &&
        kibanaButtonRef.current &&
        !kibanaButtonRef.current.contains(event.target as Node)
      ) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const removeQaPair = (index: number) => {
    if (formik.values.qaItems.length > 1) {
      const newQaItems = [...formik.values.qaItems];
      newQaItems.splice(index, 1);
      formik.setFieldValue("qaItems", newQaItems);
    }
    setOpenMenuId(null);
  };

  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      qaItems: Yup.array().of(
        Yup.object().shape({
          question: Yup.string()
            .required("Question is required")
            .test(
              "not-empty-if-answer",
              "Question is required when answer is filled",
              function (value) {
                const answer = this.parent.answer;
                return !answer?.trim() || value?.trim()?.length > 0;
              }
            ),
          answer: Yup.string()
            .required("Answer is required")
            .test(
              "not-empty-if-question",
              "Answer is required when question is filled",
              function (value) {
                const question = this.parent.question;
                return !question?.trim() || value?.trim()?.length > 0;
              }
            ),
        })
      ),
    });
  }, []);

  const formik = useFormik<QAFormValues>({
    initialValues: {
      qaItems: [{ question: "", answer: "" }],
    },
    validationSchema,
    onSubmit: async (values) => {
      const errors = await formik.validateForm();
      if (Object.keys(errors).length > 0) {
        return;
      }

      const validQaItems = values.qaItems.filter(
        (item) => item.question.trim() !== "" && item.answer.trim() !== ""
      );

      if (validQaItems.length > 0) {
        try {
          await createCustomQa({
            chunks: validQaItems.map((item) => ({
              question: item.question,
              answer: item.answer,
              namespace: {
                name: selectedProperty?.name || "",
                id: selectedProperty?.id || "",
              },
            })),
          }).unwrap();

          trackEvents("custom_qna_added", {
            document_type: selectedProperty?.name ?? "Generic",
            file_name: getDocumentName(selectedProperty?.name ?? "Generic"),
            edited_by: userId,
            version: "latest",
            edit_type: "custom_qa_added",
            org_id: orgId,
          });
          customToast(
            "Custom Q&A added successfully",
            "success",
            undefined,
            undefined,
            "bottom-left"
          );
        } catch (error) {
          console.error("Error adding custom Q&A", error);
          customToast(
            "Error adding custom Q&A",
            "error",
            undefined,
            undefined,
            "bottom-left"
          );
        } finally {
          formik.resetForm();
          setScrollToSection?.("faq");
          onClose();
        }
      }
    },
  });

  useEffect(() => {
    qaItemRefs.current = qaItemRefs.current.slice(
      0,
      formik.values.qaItems.length
    );
  }, [formik.values.qaItems.length]);

  const getFieldError = (
    index: number,
    field: "question" | "answer"
  ): string | undefined => {
    const path = `qaItems.${index}.${field}`;
    const touched = getIn(formik.touched, path);
    const error = getIn(formik.errors, path) as string | undefined;
    if (touched && error) return error;
    return undefined;
  };

  const addQaPair = () => {
    const newLength = formik.values.qaItems.length;
    formik.setFieldValue("qaItems", [
      ...formik.values.qaItems,
      { question: "", answer: "" },
    ]);

    // Scroll to the newly added QA pair
    setTimeout(() => {
      qaItemRefs.current[newLength]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }, 100);
  };

  const isFormValid = useMemo(() => {
    const hasValidPair = formik.values.qaItems.some(
      (item) => item.question.trim() !== "" && item.answer.trim() !== ""
    );

    const hasPartialPair = formik.values.qaItems.some(
      (item) =>
        (item.question.trim() !== "" && item.answer.trim() === "") ||
        (item.question.trim() === "" && item.answer.trim() !== "")
    );

    return hasValidPair && !hasPartialPair;
  }, [formik.values.qaItems]);

  const handlePropertySelection = useCallback(
    (value: string, option: SearchableDropdownOption) => {
      if (value && option) {
        setSelectedProperty({
          id: value,
          name: option.label ?? "",
        });
      }
    },
    []
  );

  const getDocumentName = (propertyName: string = "Generic") => {
    if (propertyName === "Generic") return "verbaflo_q&a.docx";
    const normalizedName = propertyName
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/-/g, "_")
      .replace(/\./g, "_");
    return `${normalizedName}_custom_faq.docx`;
  };

  return (
    <form
      onSubmit={formik.handleSubmit}
      className="flex flex-col gap-2.5 h-full overflow-hidden"
    >
      {/* Fixed Header Section */}
      <div className="shrink-0 flex flex-col gap-2.5 pt-[18px]">
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-col gap-2">
            <QuestionandanswerIcon className="w-[34px] h-[34px] text-verba-blue dark:text-verba-blue-dk" />
            <h2 className="text-2xl font-medium text-verba-indigo dark:text-verba-indigo-dk">
              Add Custom FAQs
            </h2>
          </div>
        </div>

        <p className="text-base text-verba-indigo dark:text-verba-indigo-dk">
          Add and manage your own FAQs directly within the platform. These
          custom questions will be added to the following document
        </p>

        <div className="mt-[26px] space-y-[18px]">
          <SearchableDropdown
            options={transformPropertiesOptions}
            value={selectedProperty?.id ?? "-1"}
            onChange={handlePropertySelection}
            className="w-fit min-w-[264px]"
            sectionHeading="All Properties"
            label="Select Document Type/Property"
            labelClassName="text-base text-verba-indigo dark:text-verba-indigo-dk font-medium whitespace-nowrap"
            selectedOptionContainerClassName="w-full"
          />
          <div className="border border-selected-option-highlight dark:border-selected-option-highlight-dk bg-card-highlight-2 dark:bg-card-highlight-2-dk rounded-[8px]  py-2 px-3 flex flex-col gap-0.5">
            <p className="text-dark-gray dark:text-dark-gray-dk text-md">
              Please note these custom questions will be added to the following
              document
            </p>
            <div className="flex items-center gap-2">
              <img
                src={getFileTypeIcon(
                  getDocumentName(selectedProperty?.name ?? "Generic")
                    .split(".")
                    .pop()
                    ?.toUpperCase() ?? "DOCX"
                )}
                alt="pdf"
                className="w-[18px] h-[18px]"
              />
              <span className="text-md text-heading-text dark:text-heading-text-dk font-medium">
                {getDocumentName(selectedProperty?.name ?? "Generic")}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable QA Items Section */}
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-3.5 mt-[29px] pr-1">
        {formik.values.qaItems.map((item, index) => (
          <div
            key={index}
            ref={(el) => (qaItemRefs.current[index] = el)}
            className="flex flex-col gap-3.5"
          >
            <div className="bg-verba-sky dark:bg-verba-sky-dk rounded-[6px] w-fit flex items-center justify-center py-1.5 px-3">
              <span className="text-md font-semibold text-fixed-white-light-and-dark-mode dark:text-fixed-white-light-and-dark-mode-dk whitespace-nowrap">
                Question {index + 1}
              </span>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-md text-verba-field-label dark:text-verba-field-label-dk ">
                  Enter Question
                </span>
                {index > 0 && (
                  <div className="relative">
                    <button
                      ref={kibanaButtonRef}
                      type="button"
                      onClick={() =>
                        setOpenMenuId(openMenuId === index ? null : index)
                      }
                      className="flex items-center justify-center w-[28px] h-[28px] rounded-[4px] hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors"
                      aria-label="More options"
                    >
                      <ThreeDotsVerticalIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk" />
                    </button>

                    {openMenuId === index && (
                      <div
                        ref={kibanaMenuRef}
                        className="flex flex-col gap-[4px] absolute right-0 top-[32px] z-99999 w-[150px] h-fit bg-popper-bg dark:bg-popper-bg-dk backdrop-blur-[10px] border border-border-gray dark:border-border-gray-dk rounded-[8px] shadow-popper-blur-effect"
                      >
                        <Button
                          innerText="Remove question"
                          type="button"
                          Icon={TrashFilledIcon}
                          onClick={() => removeQaPair(index)}
                          buttonContainerClass="rounded-[6px] w-full justify-start hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors whitespace-nowrap  px-[12px] py-[12px]"
                          innerTextClass="text-md font-normal text-verba-red-alert dark:text-verba-red-alert-dk"
                          iconClass="w-[18px] h-[18px] text-verba-red-alert dark:text-verba-red-alert-dk"
                          iconLeft={true}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              <Input
                name={`qaItems.${index}.question`}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                secondaryContainerClassName={`text-md p-2 rounded-[8px] text-heading-text dark:text-heading-text-dk border ${
                  getFieldError(index, "question")
                    ? "border-verba-red-alert dark:border-verba-red-alert-dk"
                    : "border-border-gray dark:border-border-gray-dk"
                }`}
                placeholder="Q: Type your question"
                value={item.question}
                error={Boolean(getFieldError(index, "question"))}
                errorMessage={getFieldError(index, "question")}
              />
            </div>

            <TextArea
              name={`qaItems.${index}.answer`}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              placeholder="Type your answer..."
              labelClassName="text-md text-verba-field-label dark:text-verba-field-label-dk"
              secondaryContainerClassName={`text-md p-2 rounded-[8px] text-heading-text dark:text-heading-text-dk border ${
                getFieldError(index, "answer")
                  ? "border-verba-red-alert dark:border-verba-red-alert-dk"
                  : "border-border-gray dark:border-border-gray-dk"
              }`}
              label="Enter Answer"
              value={item.answer}
              error={Boolean(getFieldError(index, "answer"))}
              errorMessage={getFieldError(index, "answer")}
            />

            <div
              className={`border-b border-border-gray dark:border-border-gray-dk my-[30px] ${
                index === formik.values.qaItems.length - 1 ? "hidden" : ""
              }`}
            >
              {" "}
            </div>
          </div>
        ))}
        {formik.values.qaItems.length === 1 && (
          <div className="shrink-0 flex flex-wrap justify-between gap-2">
            <Button
              type="button"
              innerText="Add another question"
              Icon={AddIcon}
              onClick={addQaPair}
              innerTextClass="text-md text-verba-blue dark:text-verba-blue-dk whitespace-nowrap"
              buttonContainerClass="rounded-[8px] py-[8px] flex justify-start gap-[8px] mt-3"
              iconClass="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk shrink-0"
              iconLeft={true}
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button
                type="button"
                onClick={onClose}
                innerText="Cancel"
                buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
              />
              <Button
                type="submit"
                innerText={`Save ${formik.values.qaItems.length} questions`}
                isLoading={isCreatingCustomQa}
                disabled={!isFormValid}
                buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed hover:bg-verba-blue dark:hover:bg-verba-blue-dk text-pure-white dark:text-pure-white-dk hover:text-pure-white-dk dark:hover:text-pure-white"
                innerTextClass="text-md font-medium whitespace-nowrap"
              />
            </div>
          </div>
        )}
      </div>

      {/* Fixed Buttons at Bottom */}
      {formik.values.qaItems.length > 1 && (
        <div className="shrink-0 flex flex-wrap justify-between gap-2">
          <Button
            type="button"
            innerText="Add another question"
            Icon={AddIcon}
            onClick={addQaPair}
            innerTextClass="text-md text-verba-blue dark:text-verba-blue-dk whitespace-nowrap"
            buttonContainerClass="rounded-[8px] py-[8px] flex justify-start gap-[8px]"
            iconClass="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk shrink-0"
            iconLeft={true}
          />
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              onClick={onClose}
              innerText="Cancel"
              buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
              innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
            />
            <Button
              type="submit"
              innerText={`Save ${formik.values.qaItems.length} questions`}
              isLoading={isCreatingCustomQa}
              disabled={!isFormValid}
              buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed hover:bg-verba-blue dark:hover:bg-verba-blue-dk text-pure-white dark:text-pure-white-dk hover:text-pure-white-dk dark:hover:text-pure-white"
              innerTextClass="text-md font-medium whitespace-nowrap"
            />
          </div>
        </div>
      )}
    </form>
  );
};

export default AddCustomQAContent;
